import React, { useState, useEffect, useCallback, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  X,
  Download,
  Edit,
  Save,
  Trash2,
  Calendar,
  HardDrive,
} from "lucide-react";
import { fileApi } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "react-hot-toast";
import { truncateFileName } from "../utils/textUtils";
import { useModal } from "../hooks/useModal";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const FileViewer = ({
  file,
  isOpen,
  onClose,
  onFileUpdated,
  startInEditMode = false,
  collectionName,
  files = [],
  onNavigate,
  rejectingChatId = null,
}) => {
  const { translate, language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(file?.file_type === "pdf" ? 1.4 : 1.0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasActivatedEditMode = useRef(false);
  const panelRef = useRef(null);

  // Find current file index for prev/next navigation
  const currentIndex = files.findIndex((f) => f.path === file?.path);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1 && currentIndex >= 0;

  // Animate in on open
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  const loadFileContent = useCallback(async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setContent(null);

    try {
      if (file.file_type === "pdf") {
        const blob = await fileApi.getFileContentAsBlob(
          file.path,
          collectionName
        );
        const url = URL.createObjectURL(blob);
        setContent({ type: "pdf", url });
      } else {
        const response = await fileApi.getFileContent(
          file.path,
          collectionName
        );
        setContent({ type: "text", data: response.content });
      }
    } catch (err) {
      console.error("Error loading file:", err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          translate("failedToLoadFileContent")
      );
    } finally {
      setLoading(false);
    }
  }, [file, collectionName, translate]);

  useEffect(() => {
    if (isOpen && file) {
      loadFileContent();
      setEditedContent("");
      setHasUnsavedChanges(false);
      hasActivatedEditMode.current = false;
    }
  }, [isOpen, file, loadFileContent]);

  useEffect(() => {
    if (
      startInEditMode &&
      content?.type === "text" &&
      !hasActivatedEditMode.current
    ) {
      setIsEditing(true);
      setEditedContent(content.data);
      setHasUnsavedChanges(false);
      hasActivatedEditMode.current = true;
    }
  }, [startInEditMode, content]);

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleDownload = () => {
    if (file.file_type === "pdf" && content?.url) {
      const link = document.createElement("a");
      link.href = content.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (content?.type === "text") {
      const blob = new Blob([content.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const cleanup = useCallback(() => {
    if (content?.type === "pdf" && content?.url) {
      URL.revokeObjectURL(content.url);
    }
    setContent(null);
    setError(null);
    setNumPages(null);
    setScale(file?.file_type === "pdf" ? 1.4 : 1.0);
    setIsEditing(false);
    setEditedContent("");
    setHasUnsavedChanges(false);
  }, [content, file?.file_type]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      if (window.confirm(translate("unsavedChanges"))) {
        setIsAnimating(false);
        setTimeout(() => {
          cleanup();
          setIsEditing(false);
          onClose();
        }, 300);
      }
    } else {
      setIsAnimating(false);
      setTimeout(() => {
        cleanup();
        setIsEditing(false);
        onClose();
      }, 300);
    }
  }, [hasUnsavedChanges, translate, cleanup, onClose]);

  const handlePrev = useCallback(() => {
    if (hasPrev && onNavigate) {
      cleanup();
      onNavigate(files[currentIndex - 1]);
    }
  }, [hasPrev, onNavigate, cleanup, files, currentIndex]);

  const handleNext = useCallback(() => {
    if (hasNext && onNavigate) {
      cleanup();
      onNavigate(files[currentIndex + 1]);
    }
  }, [hasNext, onNavigate, cleanup, files, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || isEditing) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" && hasPrev) handlePrev();
      if (e.key === "ArrowRight" && hasNext) handleNext();
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isEditing, hasPrev, hasNext, handlePrev, handleNext, handleClose]);

  const handleEdit = () => {
    if (content?.type === "text") {
      setEditedContent(content.data);
      setIsEditing(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      try {
        const confirmed = window.confirm(translate("unsavedChanges"));
        if (confirmed) {
          setIsEditing(false);
          setEditedContent(content?.data || "");
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        setIsEditing(false);
        setEditedContent(content?.data || "");
        setHasUnsavedChanges(false);
      }
    } else {
      setIsEditing(false);
      setEditedContent(content?.data || "");
      setHasUnsavedChanges(false);
    }
  };

  const handleSave = async () => {
    if (!isEditing || !hasUnsavedChanges) return;

    setSaving(true);

    try {
      const response = await fileApi.updateFileContent(
        file.path,
        editedContent,
        collectionName
      );

      if (
        response.success ||
        response.message?.includes("successfully") ||
        response.message === "File uploaded successfully"
      ) {
        setContent({ type: "text", data: editedContent });
        setIsEditing(false);
        setHasUnsavedChanges(false);
        toast.success(translate("fileSaved"));
        if (onFileUpdated) {
          onFileUpdated(file.path, response);
        }
      } else {
        throw new Error(
          response.message || response.error || translate("failedToSaveFile")
        );
      }
    } catch (err) {
      console.error("Error saving file:", err);

      let errorMessage = translate("failedToSaveFile");

      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;

        switch (status) {
          case 400:
            errorMessage =
              data?.detail || data?.message || translate("failedToSaveFile");
            break;
          case 401:
            errorMessage = translate("unauthorizedToEditFile");
            break;
          case 403:
            errorMessage = translate("permissionDeniedToEditFile");
            break;
          case 404:
            errorMessage = translate("fileNotFoundForEdit");
            break;
          case 413:
            errorMessage = translate("fileTooLargeToEdit");
            break;
          case 500:
            errorMessage = translate("serverErrorWhileSaving");
            break;
          default:
            errorMessage =
              data?.detail || data?.message || translate("failedToSaveFile");
        }
      } else if (err.request) {
        errorMessage = translate("networkErrorWhileSaving");
      } else {
        errorMessage = err.message || translate("failedToSaveFile");
      }

      toast.error(errorMessage);
      setHasUnsavedChanges(true);
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (e) => {
    setEditedContent(e.target.value);
    setHasUnsavedChanges(e.target.value !== content?.data);
  };

  const handleDeleteFile = () => {
    if (!file) return;
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!file) return;

    setDeleting(true);
    try {
      const response = await fileApi.deleteFile(file.path, collectionName, rejectingChatId || null);
      if (response.success) {
        // Numa campanha da VTEX o gesto é uma rejeição para esta loja, e o ficheiro,
        // que é partilhado, só sai quando não sobra loja nenhuma a publicá-lo. Dizer
        // "eliminado" quando ele fica era falso, e a lista que o onFileUpdated refaz
        // mostrava-o na mesma.
        toast.success(
          translate(
            response.action !== "rejected"
              ? "fileDeleted"
              : response.file_removed
                ? "campaignRejectedAndRemoved"
                : "campaignRejectedForStore"
          )
        );
        setShowDeleteConfirm(false);
        onClose();
        if (onFileUpdated) {
          onFileUpdated(file.path, { type: "deleted" });
        }
      } else {
        throw new Error(response.message || translate("failedToDeleteFile"));
      }
    } catch (err) {
      console.error("Error deleting file:", err);
      // O detalhe do servidor, quando o há: uma campanha da VTEX partilhada por duas
      // lojas é recusada com 409 e a frase diz o que fazer a seguir. A mensagem
      // genérica escondia-a.
      toast.error(err.response?.data?.detail || translate("failedToDeleteFile"));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isEditing) {
      handleClose();
    }
  };

  const deleteConfirmModalRef = useModal(showDeleteConfirm, () =>
    setShowDeleteConfirm(false)
  );

  // File type color helpers
  const getFileColor = (type) => {
    if (type === "pdf") return { bg: "rgba(239,68,68,0.08)", fg: "#ef4444" };
    if (type === "markdown") return { bg: "rgba(59,130,246,0.08)", fg: "#3b82f6" };
    return { bg: "rgba(34,197,94,0.08)", fg: "#22c55e" };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "--";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const locale = language === "pt" ? "pt-PT" : "en-GB";
    return new Date(dateStr).toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  const fileColor = getFileColor(file.file_type);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300"
        style={{
          backgroundColor: isAnimating ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0)",
        }}
        onClick={handleOverlayClick}
      >
        {/* Modal */}
        <div
          ref={panelRef}
          className="w-full flex flex-col rounded-2xl overflow-hidden"
          style={{
            maxWidth: "1080px",
            height: "min(92vh, 100%)",
            backgroundColor: "var(--color-surface)",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            border: "1px solid var(--color-border)",
            transform: isAnimating ? "scale(1)" : "scale(0.95)",
            opacity: isAnimating ? 1 : 0,
            transition: "transform 300ms cubic-bezier(0.32, 0.72, 0, 1), opacity 200ms ease",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex-shrink-0 px-6 py-4"
            style={{ borderBottom: "1px solid var(--color-border)" }}
          >
            {/* File info row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="flex-shrink-0">
                  <svg width="28" height="32" viewBox="0 0 28 32" fill="none">
                    <path
                      d="M2 3a2 2 0 012-2h13l9 9v19a2 2 0 01-2 2H4a2 2 0 01-2-2V3z"
                      stroke={fileColor.fg}
                      strokeWidth="1.5"
                      fill="none"
                    />
                    <path
                      d="M17 1v6a2 2 0 002 2h6"
                      stroke={fileColor.fg}
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                    />
                    <path
                      d="M7 15h14M7 19h14M7 23h8"
                      stroke={fileColor.fg}
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      opacity="0.5"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h2
                    className="text-base font-semibold truncate"
                    style={{ color: "var(--color-text-primary)" }}
                    title={file.name}
                  >
                    {isEditing
                      ? translate("editingFile", {
                          fileName: truncateFileName(file.name, 50),
                        })
                      : truncateFileName(file.name, 50)}
                  </h2>
                  <div className="flex items-center space-x-3 mt-0.5">
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full capitalize"
                      style={{
                        backgroundColor: fileColor.bg,
                        color: fileColor.fg,
                      }}
                    >
                      {file.file_type}
                    </span>
                    {file.size && (
                      <span className="flex items-center space-x-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
                        <HardDrive className="h-3 w-3" />
                        <span>{formatFileSize(file.size)}</span>
                      </span>
                    )}
                    {file.created_at && (
                      <span className="flex items-center space-x-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(file.created_at)}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                {!isEditing && content?.type === "text" && (
                  <button
                    onClick={handleEdit}
                    className="btn-secondary flex items-center space-x-1.5 px-3 py-1.5 text-sm"
                  >
                    <Edit className="h-3.5 w-3.5" />
                    <span>{translate("edit")}</span>
                  </button>
                )}
                {isEditing && (
                  <>
                    {!saving && (
                      <button
                        onClick={handleCancelEdit}
                        className="btn-secondary flex items-center space-x-1.5 px-3 py-1.5 text-sm"
                      >
                        <X className="h-3.5 w-3.5" />
                        <span>{translate("cancel")}</span>
                      </button>
                    )}
                    <button
                      onClick={handleSave}
                      disabled={saving || !hasUnsavedChanges}
                      className="btn-primary flex items-center space-x-1.5 px-3 py-1.5 text-sm"
                    >
                      {saving ? (
                        <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                      <span>
                        {saving ? translate("saving") : translate("save")}
                      </span>
                    </button>
                  </>
                )}
                {!isEditing && (
                  <button
                    onClick={handleDownload}
                    className="btn-secondary flex items-center space-x-1.5 px-3 py-1.5 text-sm"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span>{translate("download")}</span>
                  </button>
                )}
                {!isEditing && (
                  <button
                    onClick={handleDeleteFile}
                    className="btn-destructive flex items-center space-x-1.5 px-3 py-1.5 text-sm"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>{translate("delete")}</span>
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="hover-surface p-1.5 rounded-lg transition-all ml-1"
                  style={{ color: "var(--color-text-muted)" }}
                  title="Close (Esc)"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0" style={{ backgroundColor: "var(--color-surface-raised)" }}>
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div
                  className="h-8 w-8 border-2 border-t-transparent rounded-full animate-spin"
                  style={{
                    borderColor: "var(--color-mint)",
                    borderTopColor: "transparent",
                  }}
                ></div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ backgroundColor: "rgba(239,68,68,0.08)" }}
                  >
                    <X className="h-6 w-6" style={{ color: "var(--color-error)" }} />
                  </div>
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {translate("errorLoadingFile")}
                  </div>
                  <div className="text-sm text-gray-500">{error}</div>
                </div>
              </div>
            )}

            {content && !loading && !error && (
              <>
                {content.type === "text" && (
                  <div className="p-5">
                    {isEditing ? (
                      <textarea
                        value={editedContent}
                        onChange={handleContentChange}
                        className="w-full p-4 text-sm font-mono rounded-xl resize-none"
                        style={{
                          minHeight: "calc(92vh - 160px)",
                          color: "var(--color-text-primary)",
                          backgroundColor: "var(--color-surface)",
                          border: "1px solid var(--color-border)",
                          outline: "none",
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = "var(--color-mint)";
                          e.currentTarget.style.boxShadow = "0 0 0 3px var(--color-mint-muted)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = "var(--color-border)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                        placeholder={translate("editFile")}
                      />
                    ) : (
                      <div
                        className="rounded-xl p-5 text-sm font-mono leading-relaxed"
                        style={{
                          backgroundColor: "var(--color-surface)",
                          border: "1px solid var(--color-border)",
                          color: "var(--color-text-primary)",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {content.data}
                      </div>
                    )}
                  </div>
                )}

                {content.type === "pdf" && (
                  <div className="flex flex-col h-full">
                    {/* PDF Controls */}
                    <div
                      className="flex items-center justify-between px-5 py-2.5 flex-shrink-0"
                      style={{
                        borderBottom: "1px solid var(--color-border)",
                        backgroundColor: "var(--color-surface)",
                      }}
                    >
                      <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        {numPages || "--"}{" "}
                        {translate("page", {
                          count: numPages || 0,
                          plural: numPages !== 1 ? "s" : "",
                        })}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                          className="btn-secondary px-2 py-0.5 text-xs"
                        >
                          -
                        </button>
                        <span
                          className="text-xs w-10 text-center"
                          style={{ color: "var(--color-text-primary)" }}
                        >
                          {Math.round(scale * 100)}%
                        </span>
                        <button
                          onClick={() => setScale(Math.min(2.0, scale + 0.1))}
                          className="btn-secondary px-2 py-0.5 text-xs"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* PDF Viewer */}
                    <div
                      className="flex-1 overflow-y-auto p-5"
                      style={{ backgroundColor: "var(--color-surface-raised)" }}
                    >
                      <div className="flex flex-col items-center space-y-4">
                        <Document
                          file={content.url}
                          onLoadSuccess={handleDocumentLoadSuccess}
                          onLoadError={(error) =>
                            setError(translate("failedToLoadPDF"))
                          }
                          loading={
                            <div className="flex items-center justify-center h-64">
                              <div
                                className="h-8 w-8 border-2 border-t-transparent rounded-full animate-spin"
                                style={{
                                  borderColor: "var(--color-mint)",
                                  borderTopColor: "transparent",
                                }}
                              ></div>
                            </div>
                          }
                        >
                          {Array.from(new Array(numPages), (el, index) => (
                            <div
                              key={`page_${index + 1}`}
                              className="mb-4 rounded-lg overflow-hidden"
                              style={{
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              }}
                            >
                              <Page
                                pageNumber={index + 1}
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                loading={
                                  <div className="flex items-center justify-center h-64">
                                    <div
                                      className="h-6 w-6 border-2 border-t-transparent rounded-full animate-spin"
                                      style={{
                                        borderColor: "var(--color-mint)",
                                        borderTopColor: "transparent",
                                      }}
                                    ></div>
                                  </div>
                                }
                              />
                            </div>
                          ))}
                        </Document>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            ref={deleteConfirmModalRef}
            className="rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4"
            style={{ backgroundColor: "var(--color-surface)" }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(239,68,68,0.08)" }}
                >
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium" style={{ color: "var(--color-text-primary)" }}>
                  {translate("deleteFile")}
                </h3>
                <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("deleteFileConfirmation", {
                    fileName: file?.name,
                  })}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm mb-2" style={{ color: "var(--color-text-secondary)" }}>
                {translate("fileToBeDeleted")}:
              </p>
              <div
                className="rounded-lg p-2"
                style={{ backgroundColor: "var(--color-surface-raised)" }}
              >
                <div className="text-sm py-1" style={{ color: "var(--color-text-primary)" }}>• {file?.name}</div>
              </div>
            </div>

            <p className="text-sm text-red-600 mb-4">
              {translate("deleteActionWarning")}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="btn-secondary px-4 py-2 text-sm"
              >
                {translate("cancel")}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="btn-destructive px-4 py-2 text-sm"
              >
                {deleting ? (
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>{translate("deleting")}</span>
                  </div>
                ) : (
                  translate("delete")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FileViewer;
